import * as React from "react";

import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "./toast";

type ToasterToast = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: "default" | "destructive" | "success";
};

type ToastContextProps = {
  toasts: ToasterToast[];
  dismiss: (toastId?: string) => void;
  toast: (props: Omit<ToasterToast, "id">) => void;
};

const ToastContext = React.createContext<ToastContextProps | undefined>(undefined);

export const useToastContext = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within ToastProviderWrapper");
  }
  return context;
};

export const ToastProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = React.useState<ToasterToast[]>([]);

  const dismiss = React.useCallback((toastId?: string) => {
    if (!toastId) {
      setToasts([]);
    } else {
      setToasts((current) => current.filter((toast) => toast.id !== toastId));
    }
  }, []);

  const toast = React.useCallback((props: Omit<ToasterToast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((current) => [{ id, ...props }, ...current]);
    setTimeout(() => dismiss(id), 3500);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toasts, dismiss, toast }}>
      <ToastProvider>
        {children}
        <ToastViewport />
        {toasts.map(({ id, title, description, action, variant }) => (
          <Toast key={id} variant={variant}>
            <div className="flex flex-col gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
            </div>
            {action && (
              <ToastAction altText="Toast action" asChild>
                {action}
              </ToastAction>
            )}
            <ToastClose onClick={() => dismiss(id)} />
          </Toast>
        ))}
      </ToastProvider>
    </ToastContext.Provider>
  );
};

export { Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport };
